import { ChangeDetectorRef, Component } from '@angular/core';
import { DedupService, DedupStatus, DedupStatusResponse } from '@app/Services/dedup.service';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { iDedupColumn, iDedupItem, iDedupRow } from '@app/Shared/interfaces/iDedup';
import { AppState, DashboardActions } from '@app/Store';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { select, Store } from '@ngrx/store';
import { combineLatest, of, Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DedupPreviewComponent, DedupPreviewData } from './dedup-preview/dedup-preview.component';
import { ColumnConfig } from '@app/Store/reducers/column-config.reducer';
import { switchMap } from 'rxjs/operators';
import { selectColumnConfigForEntity } from '@app/Store/features/column-config.feature';

@Component({
    selector: 'app-dedup-tab',
    templateUrl: './dedup-tab.component.html',
    styleUrls: ['./dedup-tab.component.scss'],
})
export class DedupTabComponent {
    // Store subscriptions
    private subscription: Subscription = new Subscription();

    // Component state
    public dedupItems: iDedupItem[] = [];
    public currentEntity: string | null = null;
    public isActive: boolean = false;
    public dedupColumns: iDedupColumn[] = [];
    public dedupRows: iDedupRow[] = [];
    public selectedEntity: any = null;

    // Column config for header names
    private columnConfig: ColumnConfig[] = [];

    // Checkbox selection state
    private selectedItems: Set<string> = new Set();
    private selectedCells: Set<string> = new Set();

    // Show only diffs state
    public showOnlyDiffs: boolean = false;

    public isPerformingDedup: boolean = false;

    // Dedup status tracking
    public dedupStatus: DedupStatus | null = null;
    public dedupStatusResponse: DedupStatusResponse | null = null;
    public isPolling: boolean = false;
    private pollingSubscription: Subscription | null = null;

    // Filtered rows getter
    public get filteredDedupRows(): iDedupRow[] {
        if (!this.showOnlyDiffs) {
            return this.dedupRows;
        }
        return this.dedupRows.filter((row) => this.hasRowDifferences(row));
    }

    // Survivor and ignore state
    private survivorRecord: string | null = null;
    private ignoredRecords: Set<string> = new Set();
    // Drag state for reordering items (columns)
    private dragStartIndex: number | null = null;
    private dragOverIndex: number | null = null;
    // Master grid column order for current entity
    private masterGridColumnOrder: string[] = [];

    // Editable cell state
    private editingCell: string | null = null; // Format: "property_itemId"
    private originalCellValue: string = '';
    private editedCellValue: string = '';
    // Track if we have local edits to prevent store overwrites
    private hasLocalModifications: boolean = false;

    // Performance optimization: Cache for expensive calculations
    private valueDifferenceCache = new Map<string, boolean>();
    private cellSelectionCache = new Map<string, boolean>();
    private recordIgnoredCache = new Map<string, boolean>();
    private rowDifferencesCache = new Map<string, boolean>();

    // Clear all caches when data changes
    private clearCaches(): void {
        this.valueDifferenceCache.clear();
        this.cellSelectionCache.clear();
        this.recordIgnoredCache.clear();
        this.rowDifferencesCache.clear();
    }

    constructor(
        private store: Store<AppState>,
        private cdr: ChangeDetectorRef,
        private alertService: AlertService,
        private dedupService: DedupService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.initializeSubscriptions();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.stopPolling();
    }

    /**
     * Stop polling for dedup status
     */
    private stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
            this.isPolling = false;
        }
    }

    /**
     * Start polling for dedup status
     */
    private startPolling(date: Date, masterTable: string, goldenKey: number): void {
        this.stopPolling(); // Stop any existing polling

        this.isPolling = true;
        this.dedupStatus = 'in_progress';
        this.cdr.detectChanges();

        this.pollingSubscription = this.dedupService.pollDedupStatus(date, masterTable, goldenKey).subscribe({
            next: (statusResponse: DedupStatusResponse) => {
                console.log('Dedup status update:', statusResponse);
                this.dedupStatusResponse = statusResponse;
                this.dedupStatus = statusResponse.status;
                this.cdr.detectChanges();

                // Handle final status
                if (statusResponse.status === 'success') {
                    this.handleDedupSuccess(statusResponse);
                } else if (statusResponse.status === 'error') {
                    this.handleDedupError(statusResponse);
                }
            },
            error: (error) => {
                console.error('Polling error:', error);
                this.isPolling = false;
                this.dedupStatus = 'error';
                this.cdr.detectChanges();
                this.alertService.notify(AlertType.Error, `Failed to check dedup status: ${error.message}`, 'Status Check Error');
            },
        });
    }
    /**
     * Handle successful deduplication
     */
    private handleDedupSuccess(statusResponse: DedupStatusResponse): void {
        this.isPolling = false;
        this.isPerformingDedup = false;
        this.stopPolling();
        this.cdr.detectChanges();
        this.alertService.notify(AlertType.Success, 'Deduplication completed successfully!', 'Success');
        // Optionally clear the dedup items or refresh data
        this.clearAllDedupItems();
    }

    /**
     * Handle deduplication error
     */
    private handleDedupError(statusResponse: DedupStatusResponse): void {
        this.isPolling = false;
        this.isPerformingDedup = false;
        this.stopPolling();
        this.cdr.detectChanges();
        const errorMessage = statusResponse.details || 'Deduplication failed with an error.';
        this.alertService.notify(AlertType.Error, errorMessage, 'Deduplication Error');
    }

    public closeStatusView(): void {
        // Stop polling if in progress
        if (this.isPolling) {
            this.stopPolling();
        }

        this.dedupStatus = null;
        this.dedupStatusResponse = null;
        this.cdr.detectChanges();
    }

    private initializeSubscriptions(): void {
        // Subscribe to dedup state and column config together
        const dedupState$ = this.store.pipe(select(dashboardFeatureSelectors.selectDedupState));

        this.subscription.add(
            combineLatest([
                dedupState$,
                dedupState$.pipe(
                    switchMap((dedupState) => (dedupState.currentEntity ? this.store.pipe(select(selectColumnConfigForEntity(dedupState.currentEntity))) : of([])))
                ),
            ]).subscribe(([dedupState, columnConfig]) => {
                // Only update dedupItems if they haven't been locally modified
                // This prevents overwriting local edits
                if (dedupState.items.length !== this.dedupItems.length || !this.hasLocalModifications) {
                    // Reset local modifications flag when new items are added
                    if (dedupState.items.length !== this.dedupItems.length) {
                        this.hasLocalModifications = false;
                    }
                    this.dedupItems = dedupState.items;
                }
                this.currentEntity = dedupState.currentEntity;
                this.isActive = dedupState.isActive;
                this.columnConfig = columnConfig || [];
                this.generateDedupDisplayData();
                this.cdr.detectChanges();
            })
        );

        // Subscribe to master grid column order changes
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)).subscribe((entity) => {
                if (entity && entity.entityName !== this.currentEntity) {
                    // Clear column order when switching entities
                    this.masterGridColumnOrder = [];
                    // Reset local modifications flag when switching entities
                    this.hasLocalModifications = false;
                }
            })
        );

        // Subscribe to dedup service for column order updates
        this.subscription.add(
            this.dedupService.addToDedup$.subscribe((payload) => {
                if (payload.propertyOrder) {
                    this.setMasterGridColumnOrder(payload.propertyOrder);
                }
            })
        );

        // Subscribe to selected entity changes to reset dedup
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)).subscribe((entity) => {
                this.selectedEntity = entity;
                if (entity && entity.entityName !== this.currentEntity) {
                    this.store.dispatch(DashboardActions.resetDedupForEntity({ entityName: entity.entityName }));
                }
            })
        );
    }

    public clearAllDedupItems(): void {
        this.store.dispatch(DashboardActions.clearDedupItems());
    }

    public removeRecordFromDedup(itemId: string): void {
        // Find the item to get its details for confirmation
        const item = this.dedupItems.find((item) => item.id === itemId);
        const itemLabel = item ? `Item ${this.dedupItems.indexOf(item) + 1}` : 'this record';

        this.alertService.confirm('Are you sure you want to remove this record from the dedup comparison?', 'Remove Record', () => {
            // Remove from survivor if it was set as survivor
            if (this.survivorRecord === itemId) {
                this.survivorRecord = null;
            }

            // Remove from ignored records
            this.ignoredRecords.delete(itemId);

            // Remove from selections
            this.selectedItems.delete(itemId);
            this.selectedCells.forEach((cellKey) => {
                if (cellKey.endsWith(`/**-*/${itemId}`)) {
                    this.selectedCells.delete(cellKey);
                }
            });

            // Dispatch action to remove from store
            this.store.dispatch(DashboardActions.removeDedupItem({ itemId }));

            this.cdr.detectChanges();
        });
    }

    private generateDedupDisplayData(): void {
        // Clear caches when data changes
        this.clearCaches();

        if (this.dedupItems.length === 0) {
            this.dedupColumns = [];
            this.dedupRows = [];
            return;
        }

        // Get all unique property names from all items
        const allProperties = new Set<string>();
        this.dedupItems.forEach((item) => {
            if (item.data) {
                Object.keys(item.data).forEach((key) => allProperties.add(key));
            }
        });

        // Order properties: first those in master grid order, then any extras
        const orderedProperties: string[] = [
            ...this.masterGridColumnOrder.filter((prop) => allProperties.has(prop)),
            ...Array.from(allProperties).filter((prop) => !this.masterGridColumnOrder.includes(prop)),
        ];

        // Create columns (properties become columns)
        this.dedupColumns = orderedProperties.map((property) => {
            const values = this.dedupItems.map((item) => item.data?.[property] || '');
            const headerName = this.getHeaderNameForProperty(property);
            return {
                field: property,
                headerName: headerName,
                type: this.getPropertyType(values),
                values: values,
            };
        });

        // Create rows (each property becomes a row)
        this.dedupRows = orderedProperties.map((property) => {
            const values: { [itemId: string]: any } = {};
            this.dedupItems.forEach((item) => {
                values[item.id] = item.data?.[property] || '';
            });
            return {
                property: property,
                values: values,
            };
        });
        this.cdr.detectChanges();
    }

    // Get header name for a property from column config, fallback to property name
    private getHeaderNameForProperty(property: string): string {
        const config = this.columnConfig.find((col) => col.field === property);
        return config?.headerName || property;
    }

    // Public method to get header name for template use
    public getHeaderName(property: string): string {
        return this.getHeaderNameForProperty(property);
    }

    private getPropertyType(values: any[]): string {
        if (values.length === 0) return 'string';

        const firstValue = values.find((v) => v !== null && v !== undefined && v !== '');
        if (firstValue === null || firstValue === undefined) return 'string';

        if (typeof firstValue === 'number') return 'number';
        if (typeof firstValue === 'boolean') return 'boolean';
        if (firstValue instanceof Date) return 'date';
        if (typeof firstValue === 'string' && !isNaN(Date.parse(firstValue))) return 'date';

        return 'string';
    }

    public isValueDifferent(values: { [itemId: string]: any }, itemId: string): boolean {
        if (this.dedupItems.length <= 1) return false;
        if (this.ignoredRecords.has(itemId)) return false;

        // Create cache key based on values and ignored records
        const cacheKey = `${JSON.stringify(values)}/**-*/${Array.from(this.ignoredRecords).sort().join(',')}/**-*/${itemId}`;

        // Check cache first
        if (this.valueDifferenceCache.has(cacheKey)) {
            return this.valueDifferenceCache.get(cacheKey)!;
        }

        // Normalize values for comparison (handle null, undefined, empty strings)
        const normalizeValue = (val: any) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toLowerCase();
        };

        // Get all active (non-ignored) values
        const activeEntries = Object.entries(values).filter(([key, value]) => !this.ignoredRecords.has(key));

        if (activeEntries.length <= 1) {
            this.valueDifferenceCache.set(cacheKey, false);
            return false;
        }

        // Find the most common value (majority value)
        const valueCounts = new Map<string, number>();
        activeEntries.forEach(([key, value]) => {
            const normalized = normalizeValue(value);
            valueCounts.set(normalized, (valueCounts.get(normalized) || 0) + 1);
        });

        // Find the value with the highest count (majority)
        let majorityValue = '';
        let maxCount = 0;
        valueCounts.forEach((count, value) => {
            if (count > maxCount) {
                maxCount = count;
                majorityValue = value;
            }
        });

        // If there's no clear majority (all values are unique), consider survivor record as reference
        if (maxCount === 1 && this.survivorRecord) {
            majorityValue = normalizeValue(values[this.survivorRecord]);
        }

        const normalizedCurrent = normalizeValue(values[itemId]);
        const result = normalizedCurrent !== majorityValue;

        // Cache the result
        this.valueDifferenceCache.set(cacheKey, result);
        return result;
    }
    public hasRowDifferences(row: iDedupRow): boolean {
        if (this.dedupItems.length <= 1) return false;

        // Create cache key based on row values and ignored records
        const cacheKey = `${row.property}/**-*/${JSON.stringify(row.values)}/**-*/${Array.from(this.ignoredRecords).sort().join(',')}`;

        // Check cache first
        if (this.rowDifferencesCache.has(cacheKey)) {
            return this.rowDifferencesCache.get(cacheKey)!;
        }

        const activeItemIds = this.dedupItems.filter((item) => !this.ignoredRecords.has(item.id)).map((item) => item.id);

        if (activeItemIds.length <= 1) {
            this.rowDifferencesCache.set(cacheKey, false);
            return false;
        }

        // Normalize values for comparison
        const normalizeValue = (val: any) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toLowerCase();
        };
        // Find the most common value (majority value)
        const valueCounts = new Map<string, number>();
        activeItemIds.forEach((id) => {
            const normalized = normalizeValue(row.values[id]);
            valueCounts.set(normalized, (valueCounts.get(normalized) || 0) + 1);
        });

        // Check if there are multiple unique values
        const result = valueCounts.size > 1;

        // Cache the result
        this.rowDifferencesCache.set(cacheKey, result);
        return result;
    }

    public getUniqueValues(row: iDedupRow): Set<string> {
        const activeItemIds = this.dedupItems.filter((item) => !this.ignoredRecords.has(item.id)).map((item) => item.id);

        // Normalize values for comparison
        const normalizeValue = (val: any) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toLowerCase();
        };
        const uniqueValues = new Set<string>();
        activeItemIds.forEach((id) => {
            const normalized = normalizeValue(row.values[id]);
            uniqueValues.add(normalized);
        });

        return uniqueValues;
    }
    public getDifferentValuesCount(row: iDedupRow): number {
        const activeItemIds = this.dedupItems.filter((item) => !this.ignoredRecords.has(item.id)).map((item) => item.id);

        if (activeItemIds.length <= 1) return 0;

        // Normalize values for comparison
        const normalizeValue = (val: any) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toLowerCase();
        };

        // Find the most common value (majority value)
        const valueCounts = new Map<string, number>();
        activeItemIds.forEach((id) => {
            const normalized = normalizeValue(row.values[id]);
            valueCounts.set(normalized, (valueCounts.get(normalized) || 0) + 1);
        });

        // Find the value with the highest count (majority)
        let majorityValue = '';
        let maxCount = 0;
        valueCounts.forEach((count, value) => {
            if (count > maxCount) {
                maxCount = count;
                majorityValue = value;
            }
        });

        // If there's no clear majority (all values are unique), consider survivor record as reference
        if (maxCount === 1 && this.survivorRecord) {
            majorityValue = normalizeValue(row.values[this.survivorRecord]);
        }

        // Count how many values differ from the majority
        let differentCount = 0;
        activeItemIds.forEach((id) => {
            const normalized = normalizeValue(row.values[id]);
            if (normalized !== majorityValue) {
                differentCount++;
            }
        });

        return differentCount;
    }
    // Checkbox selection methods
    public isItemSelected(itemId: string): boolean {
        return this.selectedItems.has(itemId);
    }

    public toggleItemSelection(itemId: string): void {
        if (this.selectedItems.has(itemId)) {
            this.selectedItems.delete(itemId);
            // Remove all cells for this item
            this.selectedCells.forEach((cellKey) => {
                if (cellKey.endsWith(`_${itemId}`)) {
                    this.selectedCells.delete(cellKey);
                }
            });
        } else {
            // Clear selectedItems for items whose cells will be deselected
            this.dedupItems.forEach((item) => {
                if (item.id !== itemId) {
                    this.selectedItems.delete(item.id);
                }
            });

            this.selectedItems.add(itemId);
            // Select all cells for this item, removing cells in those rows for other items
            this.dedupRows.forEach((row) => {
                // Remove cells in this row for other items
                this.dedupItems.forEach((item) => {
                    if (item.id !== itemId) {
                        this.selectedCells.delete(`${row.property}/**-*/${item.id}`);
                    }
                });
                // Add cell for this item
                this.selectedCells.add(`${row.property}/**-*/${itemId}`);
            });
        }
        this.cdr.detectChanges();
    }
    public isCellSelected(property: string, itemId: string): boolean {
        return this.selectedCells.has(`${property}/**-*/${itemId}`);
    }

    public toggleCellSelection(property: string, itemId: string): void {
        const cellKey = `${property}/**-*/${itemId}`;
        if (this.selectedCells.has(cellKey)) {
            this.selectedCells.delete(cellKey);
        } else {
            // Remove any other cell in the same row (same property, different item)
            this.dedupItems.forEach((item) => {
                if (item.id !== itemId) {
                    this.selectedCells.delete(`${property}/**-*/${item.id}`);
                }
            });
            // Add the new cell selection
            this.selectedCells.add(cellKey);
        }
        // Clear caches since selection state affects calculations
        this.clearCaches();
        this.cdr.detectChanges();
    }

    public clearAllSelections(): void {
        this.selectedItems.clear();
        this.selectedCells.clear();
        this.cdr.detectChanges();
    }

    // Show only diffs toggle
    public toggleShowOnlyDiffs(): void {
        this.showOnlyDiffs = !this.showOnlyDiffs;
        this.cdr.detectChanges();
    }

    // Survivor selection methods
    public isSurvivorRecord(itemId: string): boolean {
        return this.survivorRecord === itemId;
    }
    public setSurvivorRecord(itemId: string): void {
        if (this.survivorRecord === itemId) {
            // If already survivor, unset it (don't change cell selections)
            this.survivorRecord = null;
        } else {
            // Set as new survivor
            this.survivorRecord = itemId;
            // Remove from ignored records if it was ignored
            this.ignoredRecords.delete(itemId);

            // Clear selectedItems for items whose cells will be deselected
            this.dedupItems.forEach((item) => {
                if (item.id !== itemId) {
                    this.selectedItems.delete(item.id);
                }
            });

            // Automatically select all cells for the survivor item
            this.selectedItems.add(itemId);
            this.dedupRows.forEach((row) => {
                // Remove cells in this row for other items
                this.dedupItems.forEach((item) => {
                    if (item.id !== itemId) {
                        this.selectedCells.delete(`${row.property}/**-*/${item.id}`);
                    }
                });
                // Add cell for this item
                this.selectedCells.add(`${row.property}/**-*/${itemId}`);
            });
        }
        this.cdr.detectChanges();
    }

    // Ignore record methods
    public isRecordIgnored(itemId: string): boolean {
        return this.ignoredRecords.has(itemId);
    }
    public toggleIgnoreRecord(itemId: string): void {
        if (this.ignoredRecords.has(itemId)) {
            this.ignoredRecords.delete(itemId);
        } else {
            this.ignoredRecords.add(itemId);
            // Remove from survivor if it was set as survivor
            if (this.survivorRecord === itemId) {
                this.survivorRecord = null;
            }
        }
        this.cdr.detectChanges();
    }

    // Get records ready for merge (not ignored, with survivor selected)
    public getMergeableRecords(): { survivor: iDedupItem | null; merged: iDedupItem[] } {
        const activeRecords = this.dedupItems.filter((item) => !this.ignoredRecords.has(item.id));
        const survivor = activeRecords.find((item) => item.id === this.survivorRecord) || null;
        const merged = activeRecords.filter((item) => item.id !== this.survivorRecord);

        return { survivor, merged };
    }
    // Perform deduplication
    public performDedup(): void {
        const { survivor, merged } = this.getMergeableRecords();

        // Validate survivor record is selected
        if (!survivor) {
            this.alertService.notify(AlertType.Error, 'Please select a survivor record before performing dedup.', 'Validation Error');
            return;
        }

        // Validate there are records to merge
        if (merged.length === 0) {
            this.alertService.notify(AlertType.Error, 'No records to merge. Please add more records to dedup.', 'Validation Error');
            return;
        }

        // Validate that at least one column is selected
        const selectedColumns = this.getSelectedColumns();
        if (selectedColumns.length === 0) {
            this.alertService.notify(AlertType.Error, 'Please select at least one column value.', 'Validation Error');
            return;
        }

        // Validate that each column in the grid has at least one item selected
        const unselectedColumns = this.getUnselectedColumns();
        if (unselectedColumns.length > 0) {
            this.alertService.notify(AlertType.Error, `Please select a value for the following columns: ${unselectedColumns.join(', ')}`, 'Validation Error');
            return;
        }

        // Get the ID field value for the survivor (golden key)
        const idField = this.selectedEntity?.key;
        if (!idField) {
            this.alertService.notify(AlertType.Error, 'Unable to determine ID field for the entity.', 'Validation Error');
            return;
        }

        const goldenKey = survivor.data[idField];
        if (!goldenKey) {
            this.alertService.notify(AlertType.Error, 'Unable to get ID value for the survivor record.', 'Validation Error');
            return;
        }

        // Prepare merging master keys (IDs of other records)
        const mergingMasterKeys = merged.map((record) => {
            const recordId = record.data[idField];
            if (!recordId) {
                throw new Error(`Unable to get ID value for record: ${record.id}`);
            }
            return parseInt(recordId, 10);
        });
        // Prepare golden fields (selected column values from any selected item)
        const goldenFields: { [key: string]: any } = {};
        selectedColumns.forEach((column) => {
            // Find the selected value for this column (from any item that has this column selected)
            const selectedValue = this.getSelectedValueForColumn(column);
            if (selectedValue !== undefined && selectedValue !== null) {
                goldenFields[column] = selectedValue;
            }
        });

        const previewData: DedupPreviewData = {
            survivor: survivor,
            merged: merged,
            goldenFields: goldenFields,
            goldenKey: parseInt(goldenKey, 10),
            entityName: this.currentEntity!,
            idField: idField,
            mergingMasterKeys: mergingMasterKeys,
        };

        // Show preview dialog
        const dialogRef = this.dialog.open(DedupPreviewComponent, {
            width: '900px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            height: 'auto',
            data: previewData,
            disableClose: true,
            panelClass: 'dedup-preview-dialog',
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.confirmed) {
                // Prepare dedup API request
                const dedupRequest = {
                    merging_master_keys: mergingMasterKeys,
                    golden_fields: goldenFields,
                };

                console.log('Performing dedup with request:', dedupRequest);

                this.isPerformingDedup = true;
                this.dedupStatus = null;
                this.dedupStatusResponse = null;
                this.cdr.detectChanges();
                // Use current date for the dedup operation
                const dedupDate = new Date();

                // Call the dedup API
                this.dedupService.performDedup(dedupDate, this.currentEntity!, goldenKey, dedupRequest).subscribe({
                    next: (response) => {
                        console.log('Dedup API POST success:', response);
                        // Start polling for status after successful POST
                        this.startPolling(dedupDate, this.currentEntity!, goldenKey);
                    },
                    error: (error) => {
                        console.error('Dedup API POST error:', error);
                        this.isPerformingDedup = false;
                        this.dedupStatus = 'error';
                        this.cdr.detectChanges();
                        this.alertService.notify(AlertType.Error, `Deduplication request failed: ${error.message}`, 'API Error');
                    },
                });
            }
        });
    }
    // Expose Object.keys to template
    public Object = Object;

    // Check if a property is the ID field (not editable)
    public isIdField(property: string): boolean {
        return this.selectedEntity && this.selectedEntity.key === property;
    }

    // Get selected columns (properties that have at least one selected cell)
    private getSelectedColumns(): string[] {
        const selectedColumns = new Set<string>();

        this.selectedCells.forEach((cellKey) => {
            const [property, itemId] = cellKey.split('/**-*/');
            if (property && itemId) {
                selectedColumns.add(property);
            }
        });

        return Array.from(selectedColumns);
    }
    // Get columns that don't have any item selected for them
    private getUnselectedColumns(): string[] {
        const unselectedColumns: string[] = [];

        // Get all columns from the dedup rows
        const allColumns = this.dedupRows.map((row) => row.property);

        allColumns.forEach((column) => {
            // Check if any item has this column selected
            let hasSelectedItem = false;
            this.dedupItems.forEach((item) => {
                if (this.isCellSelected(column, item.id)) {
                    hasSelectedItem = true;
                }
            });

            if (!hasSelectedItem) {
                unselectedColumns.push(column);
            }
        });

        return unselectedColumns;
    }

    // Get the selected value for a specific column (from any item that has this column selected)
    private getSelectedValueForColumn(column: string): any {
        // Find the first item that has this column selected and return its value
        for (const item of this.dedupItems) {
            if (this.isCellSelected(column, item.id)) {
                return item.data[column];
            }
        }
        return null;
    }

    // Method to set master grid column order (called from master panel)
    public setMasterGridColumnOrder(columnOrder: string[]): void {
        this.masterGridColumnOrder = columnOrder;
        this.generateDedupDisplayData();
    }

    // Cell editing methods
    public startCellEdit(property: string, itemId: string): void {
        // Prevent editing ID fields
        if (this.isIdField(property)) {
            return;
        }

        const cellKey = `${property}/-**-/${itemId}`;
        this.editingCell = cellKey;

        // Find the current value
        const item = this.dedupItems.find((item) => item.id === itemId);
        this.originalCellValue = item?.data?.[property] || '';
        this.editedCellValue = this.originalCellValue;

        this.cdr.detectChanges();

        // Focus the input after change detection
        setTimeout(() => {
            const input = document.querySelector('.cell-edit-input') as HTMLInputElement;
            if (input) {
                input.focus();
                input.select();
            }
        }, 0);
    }
    public cancelCellEdit(): void {
        this.editingCell = null;
        this.originalCellValue = '';
        this.editedCellValue = '';
        this.cdr.detectChanges();
    }

    public saveCellEdit(): void {
        if (!this.editingCell) return;

        const [property, itemId] = this.editingCell.split('/-**-/');
        const item = this.dedupItems.find((item) => item.id === itemId);

        if (item && item.data) {
            // Create a deep copy of the data to avoid read-only property errors
            if (!this.hasLocalModifications) {
                // First time editing - create deep copies of all items
                this.dedupItems = this.dedupItems.map((dedupItem) => ({
                    ...dedupItem,
                    data: { ...dedupItem.data },
                }));
                // Find the updated item reference
                const updatedItem = this.dedupItems.find((updatedItem) => updatedItem.id === itemId);
                if (updatedItem) {
                    updatedItem.data[property] = this.editedCellValue;
                }
            } else {
                // Already have local modifications, just update the existing copy
                item.data[property] = this.editedCellValue;
            }

            // Mark that we have local modifications
            this.hasLocalModifications = true;

            // Clear editing state first
            this.editingCell = null;
            this.originalCellValue = '';
            this.editedCellValue = '';

            // Regenerate display data to reflect changes
            this.generateDedupDisplayData();

            // Force change detection to update the UI
            this.cdr.detectChanges();
        }
    }
    public isCellEditing(property: string, itemId: string): boolean {
        return this.editingCell === `${property}/-**-/${itemId}`;
    }

    public getCellValue(property: string, itemId: string): string {
        if (this.isCellEditing(property, itemId)) {
            return this.editedCellValue;
        }

        // Always get the current value from dedupItems (which includes any saved edits)
        const item = this.dedupItems.find((item) => item.id === itemId);
        return item?.data?.[property] || '';
    }

    public updateEditedValue(value: string): void {
        this.editedCellValue = value;
    }
    // Drag-and-drop handlers for reordering dedupItems (columns)
    public onItemDragStart(index: number, event: DragEvent): void {
        this.dragStartIndex = index;
        this.dragOverIndex = null;
        try {
            event.dataTransfer?.setData('text/plain', String(index));
        } catch (err) {
            // ignore drag errors (Safari)
            console.warn('Drag setData failed:', err);
        }
    }

    public onItemDragOver(index: number, event: DragEvent): void {
        event.preventDefault(); // Necessary to allow drop
        if (this.dragOverIndex !== index) {
            this.dragOverIndex = index;
            this.cdr.detectChanges();
        }
    }

    public onItemDrop(index: number, event: DragEvent): void {
        event.preventDefault();
        const fromIndex =
            this.dragStartIndex ??
            (() => {
                const text = event.dataTransfer?.getData('text/plain');
                return text != null ? parseInt(text, 10) : null;
            })();
        if (fromIndex == null || fromIndex === index) {
            this.dragStartIndex = null;
            this.dragOverIndex = null;
            return;
        }

        // Reorder dedupItems (columns)
        const reordered = [...this.dedupItems];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(index, 0, moved);
        this.dedupItems = reordered;

        // Trigger change detection so the columns re-render in new order
        this.cdr.detectChanges();
        // clear drag states after a short delay to allow CSS transition
        setTimeout(() => {
            this.dragStartIndex = null;
            this.dragOverIndex = null;
            this.cdr.detectChanges();
        }, 0);
    }
}
